// ============================================================================
// Debrief review & approval — who may do what, in one place
// ============================================================================
// draft → pending_review → published, with the rules about WHO can move it
// held here rather than spread across button-disabled props. A gate whose
// rules live in the UI is a gate that disagrees with itself the first time a
// second surface renders the same debrief.
//
// Rob's three answers, 2026-08-14:
//   1. SELF-APPROVAL IS ALLOWED and recorded distinctly. He is both the only
//      admin and the author; blocking it deadlocks the feature on day one.
//   2. EVERY DEBRIEF IS REVIEWED. No setting, no exemption.
//   3. This queue does not overlap "feedback due" — different objects.
//
// ⚠️ LOCKING WHILE PENDING IS LOAD-BEARING. Without it a reviewer approves one
// version and a different one publishes, which makes the approval meaningless.
// `canEdit` is the single source of that rule.
// ============================================================================

export const DEBRIEF_STATUS = {
  DRAFT: 'draft',
  PENDING: 'pending_review',
  PUBLISHED: 'published',
};

export const DEBRIEF_STATUS_LABEL = {
  draft: 'Draft',
  pending_review: 'Awaiting review',
  published: 'Published',
};

// Who wrote it. Falls back to false rather than true — an unknown author must
// not inherit author rights.
export const isAuthor = (debrief, user) =>
  Boolean(debrief?.written_by && user?.id && debrief.written_by === user.id);

// EDITING. Draft only, and only the author or an admin. Once submitted the
// content freezes for everyone, including admins: a reviewer editing what they
// are reviewing is the same problem as the author editing it.
export function canEdit(debrief, user, { admin = false } = {}) {
  if (!debrief || debrief.status !== DEBRIEF_STATUS.DRAFT) return false;
  return admin || isAuthor(debrief, user);
}

// SUBMIT for review — author or admin, from draft, and not while empty.
// An empty debrief in a review queue wastes the reviewer's trip.
export function canSubmit(debrief, user, { admin = false, hasContent = true } = {}) {
  if (!debrief || debrief.status !== DEBRIEF_STATUS.DRAFT) return false;
  if (!hasContent) return false;
  return admin || isAuthor(debrief, user);
}

// WITHDRAW — pull it back out of the queue. Author or admin. This is the
// author's escape hatch from the content freeze: they can't edit while
// pending, but they can always take it back.
export function canWithdraw(debrief, user, { admin = false } = {}) {
  if (!debrief || debrief.status !== DEBRIEF_STATUS.PENDING) return false;
  return admin || isAuthor(debrief, user);
}

// REQUEST CHANGES — admins only, and the comment is REQUIRED. A rejection with
// no reason is a round trip nobody can act on.
export function canRequestChanges(debrief, user, { admin = false } = {}) {
  return Boolean(admin) && debrief?.status === DEBRIEF_STATUS.PENDING;
}

// APPROVE + PUBLISH — admins only. Self-approval is permitted (Q1) and the
// caller records it via `selfApproved` below.
export function canApprove(debrief, user, { admin = false } = {}) {
  return Boolean(admin) && debrief?.status === DEBRIEF_STATUS.PENDING;
}

// UNPUBLISH — admins only, reason required. Returns it to draft so it can be
// corrected and resubmitted.
export function canUnpublish(debrief, user, { admin = false } = {}) {
  return Boolean(admin) && debrief?.status === DEBRIEF_STATUS.PUBLISHED;
}

// Approving your own work. Legal, and flagged so the trail can show it.
export const selfApproved = (debrief, user) => isAuthor(debrief, user);

// The status change a given action produces. One map, so a transition can't be
// implemented differently in two places.
export const NEXT_STATUS = {
  submit:          DEBRIEF_STATUS.PENDING,
  withdraw:        DEBRIEF_STATUS.DRAFT,
  request_changes: DEBRIEF_STATUS.DRAFT,
  approve:         DEBRIEF_STATUS.PUBLISHED,
  unpublish:       DEBRIEF_STATUS.DRAFT,
};

// Working days, not calendar days — a debrief submitted on Friday is not
// "3 days old" on Monday morning, and flagging it as such trains people to
// ignore the flag.
export function workingDaysSince(iso, now = new Date()) {
  if (!iso) return 0;
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return 0;
  let n = 0;
  const cur = new Date(start);
  while (cur < now) {
    cur.setDate(cur.getDate() + 1);
    const d = cur.getDay();
    if (d !== 0 && d !== 6) n += 1;
  }
  return n;
}

export const STALE_AFTER_WORKING_DAYS = 5;

// The review queue: oldest first, and flagged once it has been sitting.
// NEVER auto-approves and never expires anything — an approval that happened
// because nobody looked is not an approval.
export function reviewQueue(debriefs = [], { now = new Date() } = {}) {
  return debriefs
    .filter(d => d.status === DEBRIEF_STATUS.PENDING)
    .map(d => {
      const age = workingDaysSince(d.submitted_at, now);
      return { ...d, ageWorkingDays: age, stale: age >= STALE_AFTER_WORKING_DAYS };
    })
    .sort((a, b) => String(a.submitted_at || '').localeCompare(String(b.submitted_at || '')));
}

// Which action owners can actually be told. A free-text owner (a team, an
// outside company) is legitimate and simply doesn't notify — that is the whole
// reason owner_user_id exists alongside owner rather than replacing it.
export function notifiableOwners(actions = []) {
  const byUser = new Map();
  for (const a of actions) {
    if (!a?.owner_user_id) continue;
    if (!byUser.has(a.owner_user_id)) byUser.set(a.owner_user_id, []);
    byUser.get(a.owner_user_id).push(a);
  }
  return Array.from(byUser.entries()).map(([userId, theirActions]) => ({ userId, actions: theirActions }));
}
